import {
  Circle,
  CircleNotch,
  CheckCircle,
  MinusCircle,
  PauseCircle,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { TodoItem } from './types';

const STATUS_ICONS = {
  pending: <Circle className="h-4 w-4 text-stone-300" />,
  'in-progress': (
    <CircleNotch className="h-4 w-4 text-purple-500 animate-spin" />
  ),
  done: <CheckCircle className="h-4 w-4 text-emerald-500" weight="fill" />,
  skipped: <MinusCircle className="h-4 w-4 text-stone-400" />,
} as const;

/** An in-progress item while the agent is idle: it is waiting on the user, not working. */
const PAUSED_ICON = (
  <PauseCircle className="h-4 w-4 text-purple-300" data-testid="todo-paused" />
);

interface TodoListItemProps {
  item: TodoItem;
  subtitle?: string;
  /** False when the agent's turn has ended; in-progress items then read as paused. */
  agentActive: boolean;
}

export function TodoListItem({
  item,
  subtitle,
  agentActive,
}: TodoListItemProps) {
  const paused = item.status === 'in-progress' && !agentActive;
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span className="shrink-0 mt-0.5">
        {paused ? PAUSED_ICON : STATUS_ICONS[item.status]}
      </span>
      <div>
        <span
          className={cn(
            'text-xs leading-tight',
            item.status === 'done' && 'text-stone-500',
            item.status === 'skipped' && 'text-stone-400 line-through',
            item.status !== 'done' &&
              item.status !== 'skipped' &&
              'text-stone-700',
          )}
        >
          {item.label}
        </span>
        {item.status === 'in-progress' && (
          // Always mounted while in progress so the switch from the running
          // tool's name to "Waiting on your reply" is announced.
          <span
            aria-live="polite"
            className={cn(
              'block text-[10px] text-stone-400',
              !paused && subtitle && 'animate-pulse',
            )}
          >
            {subtitle ?? ''}
          </span>
        )}
      </div>
    </div>
  );
}
